/**
 * Field entities (Phase 1 + 2): one model for every synced record a site works on.
 *
 * Each record keeps two copies:
 *  - `server` — the last copy the server confirmed (pull, push result or an
 *    online endpoint's response);
 *  - `data`   — what screens show: `server` with every not-yet-confirmed
 *    outbox mutation for that record folded on top, oldest first.
 *
 * So a pull that brings someone else's change to another field shows up
 * immediately, while this device's queued edits stay on screen until the
 * server answers. `recompute` is the only place that derives `data`.
 *
 * Phase 2 adds the workforce: the roster (`project_worker`, editable with a
 * three-way merge), attendance (`attendance_event`, append-only: a correction
 * is a new `void` event, never an edit) and `worker_day` (derived by the
 * server, read-only here).
 *
 * Phase 3 adds cost control: bills of quantities sync down read-only
 * (`boq`, `boq_version`, `boq_section`, `boq_item` — editing a bill is desk
 * work, done online) and expenses (`expense`: recorded, edited while a draft
 * and submitted offline with `{changes, base}` updates; decisions online).
 *
 * Phase 4 adds procurement: requisitions (created, edited while a draft with
 * `{changes, base}` and submitted offline; approval online), purchase orders
 * (read-only on the device) and deliveries (received offline; immutable
 * evidence — a correction is an online adjustment, never an edit).
 *
 * Module 3 adds quality and safety. Conflict classes:
 *  - append-only events: inspection results, rectifications, toolbox
 *    attendance (every tap is a new record; the latest per item/worker counts);
 *  - editable narrative (`{changes, base}` three-way merge): inspections in
 *    progress (and their submission), NCRs, snags, incidents, toolbox talks;
 *  - read-only here: checklist templates, inspection requests, floor plans
 *    (scheduling, sign-off, verification and closure are online only).
 *
 * Module 4 adds site planning. Conflict classes:
 *  - read-only here: zones and crews (edited online by the planner);
 *  - editable narrative (`{changes, base}` three-way merge): constraints
 *    (raised offline; cleared online), visitor log entries (signed in at the
 *    gate, signed out later) and task planning fields (zone, crew, BOQ link,
 *    predecessors, planned start — same `task` updates as before);
 *  - independent rows: daily plan lines (each crew → task line is its own
 *    record, so two supervisors never clash; removal is a tombstone);
 *  - state transition: completing one's own action (idempotent, first wins).
 *
 * Module 5 adds equipment and inventory. Conflict classes:
 *  - read-only here: stock items (balances need inventory.view; costs need
 *    cost.view_internal) and equipment (with its pre-use checklist);
 *  - append-only events: stock movements (issues and returns — an issue that
 *    takes the store below zero is accepted by the server and flagged as a
 *    discrepancy for review, never refused) and pre-use checks (a failed
 *    critical item puts the machine under repair when it arrives);
 *  - editable narrative (`{changes, base}` three-way merge): material requests
 *    while nothing is issued (cancel is an idempotent transition) and
 *    equipment logs (one row per shift; locked once verified).
 *
 * Module 6 adds design management. Conflict classes:
 *  - read-only here: drawings and drawing revisions (the construction set on
 *    site — a revision superseded online arrives marked as such on the next
 *    pull) and specification sections;
 *  - editable narrative (`{changes, base}` three-way merge): RFIs while open
 *    and unanswered (raised offline; answering, closing and voiding are
 *    online), site visits while a draft (issuing the report is online) and
 *    observations (one row each, so two people on a walk never clash;
 *    converting into a snag, NCR or instruction is online).
 *
 * Module 7 adds the QS's measurement on site. Conflict classes:
 *  - read-only here: valuations (the surveyor's side's, drafts included);
 *  - independent rows with editable narrative (`{changes, base}` three-way
 *    merge): measurement-sheet rows (one per dimension line; removal is a
 *    tombstone). Refused once the valuation goes to its certificate; every
 *    certificate step and every payment is online only.
 *
 * Module 8 adds the subcontractor's daily report. Conflict class: editable
 * narrative (`{changes, base}` three-way merge per section, like the site
 * diary; manpower and tasks replaced whole); one report per company per day
 * (a second device's create is rebased as an update). Acknowledging is online.
 *
 * Module 11 adds messaging. Conflict classes:
 *  - read-only here: conversations (who takes part is the server's call);
 *  - append-only events: messages — written offline and sent in order on
 *    reconnect (the id is made here, so a resend is the same message), never
 *    merged; an edit is accepted only within 15 minutes of the server
 *    receiving the message (refused, never merged, after that). Deleting and
 *    pinning are online.
 */
import type { Table } from 'dexie';
import type { z } from 'zod';

import {
  diaryEntrySchema,
  issueSchema,
  mediaAssetSchema,
  milestoneSchema,
  progressUpdateSchema,
  stageSchema,
  taskSchema,
  type ChecklistItem,
} from '@/lib/api/field-types';
import type { MutationOperation, PullChange } from '@/lib/api/types';
import {
  boqItemSchema,
  boqSchema,
  boqSectionSchema,
  boqVersionSchema,
  expenseSchema,
} from '@/lib/api/cost-types';
import {
  deliverySchema,
  purchaseOrderSchema,
  requisitionSchema,
} from '@/lib/api/procurement-types';
import {
  attendanceEventSchema,
  engagementSchema,
  workerDaySchema,
} from '@/lib/api/workforce-types';
import {
  floorPlanSchema,
  incidentSchema,
  inspectionRequestSchema,
  inspectionResultSchema,
  inspectionSchema,
  ncrSchema,
  rectificationSchema,
  snagSchema,
  templateSchema,
  toolboxAttendanceSchema,
  toolboxTalkSchema,
} from '@/lib/api/quality-types';
import {
  actionItemSchema,
  constraintSchema,
  crewSchema,
  dailyAssignmentSchema,
  visitSchema,
  zoneSchema,
} from '@/lib/api/siteplan-types';
import {
  equipmentCheckSchema,
  equipmentLogSchema,
  equipmentSchema,
  materialRequestSchema,
  stockItemSchema,
  stockMovementSchema,
} from '@/lib/api/resources-types';
import {
  drawingRevisionSchema,
  drawingSchema,
  observationSchema,
  rfiSchema,
  siteVisitSchema,
  specSectionSchema,
} from '@/lib/api/design-types';
import { measurementSchema, valuationSchema } from '@/lib/api/commercial-types';
import { warrantyClaimSchema } from '@/lib/api/owner-types';
import { subReportSchema } from '@/lib/api/subcontracts-types';
import { conversationSchema, messageSchema } from '@/lib/api/messaging-types';

import { db, type FieldRecord, type OutboxRecord } from './db';
import { listForEntity, recordChange } from './outbox';

export const FIELD_ENTITY_TYPES = [
  'stage',
  'milestone',
  'task',
  'progress_update',
  'issue',
  'diary_entry',
  'media_asset',
  'project_worker',
  'attendance_event',
  'worker_day',
  'boq',
  'boq_version',
  'boq_section',
  'boq_item',
  'expense',
  'requisition',
  'purchase_order',
  'delivery',
  'checklist_template',
  'inspection_request',
  'inspection',
  'inspection_result',
  'ncr',
  'rectification',
  'floor_plan',
  'snag',
  'incident',
  'toolbox_talk',
  'toolbox_attendance',
  'zone',
  'crew',
  'constraint',
  'daily_assignment',
  'visit',
  'action_item',
  'stock_item',
  'stock_movement',
  'material_request',
  'equipment',
  'equipment_log',
  'equipment_check',
  'drawing',
  'drawing_revision',
  'spec_section',
  'rfi',
  'site_visit',
  'observation',
  'valuation',
  'measurement',
  'sub_daily_report',
  'warranty_claim',
  'conversation',
  'message',
] as const;
export type FieldEntityType = (typeof FIELD_ENTITY_TYPES)[number];

type AnyRecord = FieldRecord<{ id: string; project_id: string }>;
type Reducer = (
  current: Record<string, unknown> | null,
  mutation: Pick<OutboxRecord, 'operation' | 'payload' | 'entity_id' | 'project_id'>,
) => Record<string, unknown> | null;

type EntityConfig = {
  table: Table<AnyRecord, string>;
  schema: z.ZodType<{ id: string; project_id: string }>;
  reduce: Reducer;
};

const now = () => new Date().toISOString();

function changesOf(payload: Record<string, unknown>): Record<string, unknown> {
  const changes = payload['changes'];
  return changes && typeof changes === 'object' ? (changes as Record<string, unknown>) : {};
}

function created(
  mutation: Pick<OutboxRecord, 'payload' | 'entity_id' | 'project_id'>,
  extra: Record<string, unknown> = {},
) {
  return {
    ...mutation.payload,
    id: mutation.entity_id,
    project_id: mutation.project_id ?? '',
    version: null,
    ...extra,
  };
}

const updatesOnly: Reducer = (current, mutation) =>
  current && mutation.operation === 'update'
    ? { ...current, ...changesOf(mutation.payload) }
    : current;

const readOnly: Reducer = (current) => current;

const reducers: Record<FieldEntityType, Reducer> = {
  project_worker: (current, mutation) => {
    switch (mutation.operation) {
      case 'create': {
        // Engaging someone new on site makes a temporary profile for them.
        const payload = mutation.payload;
        const name = String(payload['display_name'] ?? '');
        return {
          id: mutation.entity_id,
          project_id: mutation.project_id ?? '',
          worker: {
            id: '',
            display_name: name,
            photo_url: null,
            temporary: true,
            verification_status: 'unverified',
          },
          trade: payload['trade'] ?? null,
          role_title: payload['role_title'] ?? '',
          start_date: payload['start_date'] ?? null,
          status: 'active',
          ...(payload['phone'] ? { phone: payload['phone'] } : {}),
          version: null,
        };
      }
      case 'update': {
        if (!current) return current;
        // The server edits `trade_id`; it serialises the same value as `trade`.
        const { trade_id: tradeId, ...changes } = changesOf(mutation.payload);
        return {
          ...current,
          ...changes,
          ...(tradeId !== undefined ? { trade: tradeId } : {}),
        };
      }
      default:
        return current;
    }
  },
  attendance_event: (current, mutation) => {
    switch (mutation.operation) {
      case 'create': {
        // occurred_at carries the project's UTC offset, so its date part is
        // the site's work date — the same day the server will file it under.
        const occurredAt = String(mutation.payload['occurred_at'] ?? now());
        return created(mutation, {
          work_date: occurredAt.slice(0, 10),
          voids_id: null,
          reason: '',
          has_location: mutation.payload['latitude'] != null,
          clock_suspect: false,
          created_at: now(),
        });
      }
      case 'correct':
        // A correction is a new `void` event pointing at the one it cancels.
        return (
          current ?? {
            id: mutation.entity_id,
            project_id: mutation.project_id ?? '',
            engagement_id: '',
            kind: 'void',
            method: 'correction',
            occurred_at: now(),
            work_date: null,
            voids_id: mutation.payload['voids_event_id'] ?? null,
            reason: mutation.payload['reason'] ?? '',
            replacement_occurred_at: mutation.payload['replacement_occurred_at'] ?? null,
            created_at: now(),
            version: null,
          }
        );
      default:
        return current;
    }
  },
  worker_day: readOnly,
  boq: readOnly,
  boq_version: readOnly,
  boq_section: readOnly,
  boq_item: readOnly,
  expense: (current, mutation) => {
    switch (mutation.operation) {
      case 'create': {
        // The server fills in the currency (the project's) when it answers.
        const { amount_minor: amountMinor, ...fields } = mutation.payload;
        return created(
          { ...mutation, payload: fields },
          {
            amount: { amount_minor: Number(amountMinor ?? 0), currency: '' },
            spent_on: fields['spent_on'] ?? now().slice(0, 10),
            status: 'draft',
          },
        );
      }
      case 'update': {
        if (!current) return current;
        const { amount_minor: amountMinor, ...changes } = changesOf(mutation.payload);
        const amount = current['amount'] as { amount_minor: number; currency: string };
        return {
          ...current,
          ...changes,
          ...(amountMinor !== undefined
            ? { amount: { ...amount, amount_minor: Number(amountMinor) } }
            : {}),
        };
      }
      case 'submit':
        return current && current['status'] === 'draft'
          ? { ...current, status: 'submitted' }
          : current;
      default:
        return current;
    }
  },
  requisition: (current, mutation) => {
    switch (mutation.operation) {
      case 'create':
        // The server numbers it ("R007") and records who asked when it arrives.
        return created(mutation, { status: 'draft', reference: '', number: 0 });
      case 'update':
        return current && current['status'] === 'draft'
          ? { ...current, ...changesOf(mutation.payload) }
          : current;
      case 'submit':
        return current && current['status'] === 'draft'
          ? { ...current, status: 'submitted' }
          : current;
      default:
        return current;
    }
  },
  purchase_order: readOnly,
  delivery: (current, mutation) =>
    mutation.operation === 'create' ? created(mutation, { received_by_id: null }) : current,
  checklist_template: readOnly,
  inspection_request: readOnly,
  floor_plan: readOnly,
  inspection: (current, mutation) => {
    switch (mutation.operation) {
      case 'create':
        // The server numbers it ("INS-004") and records the inspector when it arrives.
        return created(mutation, {
          status: 'in_progress',
          reference: '',
          started_at: mutation.payload['started_at'] ?? now(),
          created_at: now(),
        });
      case 'update':
        return current && current['status'] === 'in_progress'
          ? { ...current, ...changesOf(mutation.payload) }
          : current;
      case 'submit':
        return current && current['status'] === 'in_progress'
          ? {
              ...current,
              status: 'submitted',
              result: mutation.payload['result'] ?? current['result'] ?? null,
              summary: mutation.payload['summary'] ?? current['summary'] ?? '',
              signature_asset_id:
                mutation.payload['signature_asset_id'] ?? current['signature_asset_id'] ?? null,
              submitted_at: mutation.payload['submitted_at'] ?? now(),
            }
          : current;
      default:
        return current;
    }
  },
  // Append-only: a changed answer is a new result, never an edit.
  inspection_result: (current, mutation) =>
    mutation.operation === 'create'
      ? created(mutation, { recorded_at: mutation.payload['recorded_at'] ?? now() })
      : current,
  ncr: (current, mutation) => {
    switch (mutation.operation) {
      case 'create':
        return created(mutation, { status: 'raised', reference: '', created_at: now() });
      case 'update':
        return current && { ...current, ...changesOf(mutation.payload) };
      default:
        return current;
    }
  },
  rectification: (current, mutation) =>
    mutation.operation === 'create'
      ? created(mutation, {
          status: 'submitted',
          submitted_at: mutation.payload['submitted_at'] ?? now(),
        })
      : current,
  snag: (current, mutation) => {
    switch (mutation.operation) {
      case 'create':
        return created(mutation, { status: 'open', reference: '', created_at: now() });
      case 'update':
        return current && { ...current, ...changesOf(mutation.payload) };
      default:
        return current;
    }
  },
  incident: (current, mutation) => {
    switch (mutation.operation) {
      case 'create':
        return created(mutation, {
          status: 'reported',
          reference: '',
          occurred_at: mutation.payload['occurred_at'] ?? now(),
          actions: [],
          created_at: now(),
        });
      case 'update':
        return current && current['status'] !== 'closed'
          ? { ...current, ...changesOf(mutation.payload) }
          : current;
      default:
        return current;
    }
  },
  toolbox_talk: (current, mutation) => {
    switch (mutation.operation) {
      case 'create':
        return created(mutation, {
          reference: '',
          status: mutation.payload['status'] ?? 'held',
          created_at: now(),
        });
      case 'update':
        return current && { ...current, ...changesOf(mutation.payload) };
      default:
        return current;
    }
  },
  toolbox_attendance: (current, mutation) =>
    mutation.operation === 'create'
      ? created(mutation, { recorded_at: mutation.payload['recorded_at'] ?? now() })
      : current,
  zone: readOnly,
  crew: readOnly,
  constraint: (current, mutation) => {
    switch (mutation.operation) {
      case 'create':
        // The server numbers it ("CON-004") and records who raised it when it arrives.
        return created(mutation, { status: 'open', reference: '', created_at: now() });
      case 'update':
        return current && current['status'] === 'open'
          ? { ...current, ...changesOf(mutation.payload) }
          : current;
      default:
        return current;
    }
  },
  daily_assignment: (current, mutation) => {
    switch (mutation.operation) {
      case 'create':
        return created(mutation, { removed: false, created_at: now() });
      case 'update':
        return current && { ...current, ...changesOf(mutation.payload) };
      case 'remove':
        // Hidden at once; the server's tombstone removes it for good.
        return current && { ...current, removed: true };
      default:
        return current;
    }
  },
  visit: (current, mutation) => {
    switch (mutation.operation) {
      case 'create': {
        // time_in carries the project's UTC offset, so its date part is the site's day.
        const timeIn = String(mutation.payload['time_in'] ?? now());
        return created(mutation, { time_in: timeIn, visit_date: timeIn.slice(0, 10) });
      }
      case 'update':
        return current && { ...current, ...changesOf(mutation.payload) };
      default:
        return current;
    }
  },
  action_item: (current, mutation) =>
    mutation.operation === 'complete' && current && current['status'] === 'open'
      ? { ...current, status: 'done', done_at: now() }
      : current,
  stock_item: readOnly,
  // Append-only: an issue or a return is a new movement; the server owns the balance.
  stock_movement: (current, mutation) =>
    mutation.operation === 'create'
      ? created(mutation, {
          reference: '',
          delta: String(
            (mutation.payload['kind'] === 'return' ? 1 : -1) *
              Number(mutation.payload['quantity'] ?? 0),
          ),
          occurred_at: mutation.payload['occurred_at'] ?? now(),
          warnings: [],
          discrepancy: false,
        })
      : current,
  material_request: (current, mutation) => {
    switch (mutation.operation) {
      case 'create':
        // The server numbers it ("MR-004") and records the requesting party when it arrives.
        return created(mutation, { status: 'requested', reference: '', created_at: now() });
      case 'update':
        return current && current['status'] === 'requested'
          ? { ...current, ...changesOf(mutation.payload) }
          : current;
      case 'cancel':
        return current &&
          (current['status'] === 'requested' || current['status'] === 'partially_issued')
          ? { ...current, status: 'cancelled' }
          : current;
      default:
        return current;
    }
  },
  equipment: readOnly,
  equipment_log: (current, mutation) => {
    switch (mutation.operation) {
      case 'create':
        return created(mutation, {
          log_date: mutation.payload['log_date'] ?? now().slice(0, 10),
          created_at: now(),
        });
      case 'update':
        return current && !current['verified_at']
          ? { ...current, ...changesOf(mutation.payload) }
          : current;
      default:
        return current;
    }
  },
  // Append-only: a check is what was seen at that moment, never edited.
  equipment_check: (current, mutation) => {
    if (mutation.operation !== 'create') return current;
    const items = (mutation.payload['items'] as { result?: string }[] | undefined) ?? [];
    return created(mutation, {
      checked_at: mutation.payload['checked_at'] ?? now(),
      result: items.some((item) => item.result === 'fail') ? 'fail' : 'pass',
    });
  },
  drawing: readOnly,
  drawing_revision: readOnly,
  spec_section: readOnly,
  rfi: (current, mutation) => {
    switch (mutation.operation) {
      case 'create': {
        // The server numbers it ("RFI-007", "CRD-003") and routes it when it arrives; a
        // coordination item named for a company starts assigned.
        const kind = mutation.payload['kind'] ?? 'rfi';
        const assigned = kind === 'coordination' && Boolean(mutation.payload['assignee_party_id']);
        return created(mutation, {
          kind,
          status: assigned ? 'assigned' : 'open',
          reference: '',
          number: 0,
          raised_at: mutation.payload['raised_at'] ?? now(),
          created_at: now(),
        });
      }
      case 'update':
        return current &&
          (current['status'] === 'open' || current['status'] === 'assigned') &&
          !current['answered_at']
          ? { ...current, ...changesOf(mutation.payload) }
          : current;
      default:
        return current;
    }
  },
  site_visit: (current, mutation) => {
    switch (mutation.operation) {
      case 'create':
        return created(mutation, {
          status: 'draft',
          reference: '',
          number: 0,
          visit_date: mutation.payload['visit_date'] ?? now().slice(0, 10),
          created_at: now(),
        });
      case 'update':
        return current && current['status'] === 'draft'
          ? { ...current, ...changesOf(mutation.payload) }
          : current;
      default:
        return current;
    }
  },
  observation: (current, mutation) => {
    switch (mutation.operation) {
      case 'create':
        return created(mutation, { status: 'open', reference: '', number: 0, created_at: now() });
      case 'update':
        return current && current['status'] === 'open'
          ? { ...current, ...changesOf(mutation.payload) }
          : current;
      default:
        return current;
    }
  },
  sub_daily_report: (current, mutation) => {
    switch (mutation.operation) {
      case 'create':
        return created(mutation, {
          status: 'draft',
          party_id: '',
          subcontract_id: mutation.payload['subcontract_id'] ?? null,
          updated_at: now(),
        });
      case 'update':
        return current && { ...current, ...changesOf(mutation.payload) };
      case 'submit':
        return current && current['status'] === 'draft'
          ? { ...current, status: 'submitted', submitted_at: now() }
          : current;
      default:
        return current;
    }
  },
  warranty_claim: (current, mutation) => {
    switch (mutation.operation) {
      case 'create':
        return created(mutation, {
          status: 'open',
          reference: '',
          number: 0,
          raised_by_party_id: '',
          created_at: now(),
          updated_at: now(),
        });
      case 'update':
        // Editable while not answered yet (open or assigned): the server merges per field.
        return current && ['open', 'assigned'].includes(String(current['status']))
          ? { ...current, ...changesOf(mutation.payload) }
          : current;
      default:
        return current;
    }
  },
  conversation: readOnly,
  // Append-only: a message is immutable once sent, but for its author's edit window.
  message: (current, mutation) => {
    switch (mutation.operation) {
      case 'create':
        return created(mutation, {
          created_at: null,
          created_at_device: mutation.payload['created_at_device'] ?? now(),
          edited_at: null,
          removed: false,
          pinned: false,
          pinned_at: null,
          attachments: [],
          seen_by: null,
        });
      case 'update':
        return current && !current['removed']
          ? { ...current, ...changesOf(mutation.payload), edited_at: now() }
          : current;
      default:
        return current;
    }
  },
  valuation: readOnly,
  measurement: (current, mutation) => {
    switch (mutation.operation) {
      case 'create':
        return created(mutation, {
          number: 0,
          quantity: null,
          brought_forward: false,
          deleted: false,
          created_at: now(),
        });
      case 'update':
        return current && !current['deleted']
          ? { ...current, ...changesOf(mutation.payload) }
          : current;
      case 'remove':
        // Hidden from the sheet at once; the server's tombstone removes it for good.
        return current && { ...current, deleted: true };
      default:
        return current;
    }
  },
  stage: updatesOnly,
  milestone: updatesOnly,
  // Caption and owner-side sharing are editable offline.
  media_asset: updatesOnly,
  diary_entry: (current, mutation) => {
    switch (mutation.operation) {
      case 'create': {
        const { entry_date, ...fields } = mutation.payload;
        return created({ ...mutation, payload: fields }, { entry_date, status: 'draft' });
      }
      case 'update':
        return current && { ...current, ...changesOf(mutation.payload) };
      case 'submit':
        return current && current['status'] === 'draft'
          ? { ...current, status: 'submitted', submitted_at: now() }
          : current;
      default:
        return current;
    }
  },
  task: (current, mutation) => {
    switch (mutation.operation) {
      case 'create':
        return created(mutation, { status: mutation.payload['status'] ?? 'todo' });
      case 'update': {
        if (!current) return current;
        const changes = changesOf(mutation.payload);
        const next = { ...current, ...changes };
        if ('status' in changes) next['completed_at'] = changes['status'] === 'done' ? now() : null;
        return next;
      }
      case 'check_item': {
        if (!current) return current;
        const itemId = mutation.payload['item_id'];
        const done = Boolean(mutation.payload['done']);
        const checklist = (current['checklist'] as ChecklistItem[] | undefined) ?? [];
        return {
          ...current,
          checklist: checklist.map((item) => (item.id === itemId ? { ...item, done } : item)),
        };
      }
      default:
        return current;
    }
  },
  progress_update: (current, mutation) =>
    mutation.operation === 'create'
      ? created(mutation, {
          status: 'submitted',
          previous_percent: mutation.payload['base_percent'] ?? null,
          created_at: mutation.payload['captured_at_device'] ?? now(),
        })
      : current,
  issue: (current, mutation) => {
    switch (mutation.operation) {
      case 'create':
        return created(mutation, { status: 'open', created_at: now() });
      case 'update':
        return current && { ...current, ...changesOf(mutation.payload) };
      case 'start':
        return current && current['status'] === 'open'
          ? { ...current, status: 'in_progress' }
          : current;
      case 'resolve':
        return current
          ? {
              ...current,
              status: 'resolved',
              resolution_notes: String(mutation.payload['notes'] ?? ''),
              resolved_at: now(),
            }
          : current;
      default:
        return current;
    }
  },
};

const tables: Record<FieldEntityType, () => Table<AnyRecord, string>> = {
  stage: () => db.stages as unknown as Table<AnyRecord, string>,
  milestone: () => db.milestones as unknown as Table<AnyRecord, string>,
  task: () => db.tasks as unknown as Table<AnyRecord, string>,
  progress_update: () => db.progress_updates as unknown as Table<AnyRecord, string>,
  issue: () => db.issues as unknown as Table<AnyRecord, string>,
  diary_entry: () => db.diary_entries as unknown as Table<AnyRecord, string>,
  media_asset: () => db.media_assets as unknown as Table<AnyRecord, string>,
  project_worker: () => db.project_workers as unknown as Table<AnyRecord, string>,
  attendance_event: () => db.attendance_events as unknown as Table<AnyRecord, string>,
  worker_day: () => db.worker_days as unknown as Table<AnyRecord, string>,
  boq: () => db.boqs as unknown as Table<AnyRecord, string>,
  boq_version: () => db.boq_versions as unknown as Table<AnyRecord, string>,
  boq_section: () => db.boq_sections as unknown as Table<AnyRecord, string>,
  boq_item: () => db.boq_items as unknown as Table<AnyRecord, string>,
  expense: () => db.expenses as unknown as Table<AnyRecord, string>,
  requisition: () => db.requisitions as unknown as Table<AnyRecord, string>,
  purchase_order: () => db.purchase_orders as unknown as Table<AnyRecord, string>,
  delivery: () => db.deliveries as unknown as Table<AnyRecord, string>,
  checklist_template: () => db.checklist_templates as unknown as Table<AnyRecord, string>,
  inspection_request: () => db.inspection_requests as unknown as Table<AnyRecord, string>,
  inspection: () => db.inspections as unknown as Table<AnyRecord, string>,
  inspection_result: () => db.inspection_results as unknown as Table<AnyRecord, string>,
  ncr: () => db.ncrs as unknown as Table<AnyRecord, string>,
  rectification: () => db.rectifications as unknown as Table<AnyRecord, string>,
  floor_plan: () => db.floor_plans as unknown as Table<AnyRecord, string>,
  snag: () => db.snags as unknown as Table<AnyRecord, string>,
  incident: () => db.incidents as unknown as Table<AnyRecord, string>,
  toolbox_talk: () => db.toolbox_talks as unknown as Table<AnyRecord, string>,
  toolbox_attendance: () => db.toolbox_attendance as unknown as Table<AnyRecord, string>,
  zone: () => db.zones as unknown as Table<AnyRecord, string>,
  crew: () => db.crews as unknown as Table<AnyRecord, string>,
  constraint: () => db.constraints as unknown as Table<AnyRecord, string>,
  daily_assignment: () => db.daily_assignments as unknown as Table<AnyRecord, string>,
  visit: () => db.visits as unknown as Table<AnyRecord, string>,
  action_item: () => db.action_items as unknown as Table<AnyRecord, string>,
  stock_item: () => db.stock_items as unknown as Table<AnyRecord, string>,
  stock_movement: () => db.stock_movements as unknown as Table<AnyRecord, string>,
  material_request: () => db.material_requests as unknown as Table<AnyRecord, string>,
  equipment: () => db.equipment as unknown as Table<AnyRecord, string>,
  equipment_log: () => db.equipment_logs as unknown as Table<AnyRecord, string>,
  equipment_check: () => db.equipment_checks as unknown as Table<AnyRecord, string>,
  drawing: () => db.drawings as unknown as Table<AnyRecord, string>,
  drawing_revision: () => db.drawing_revisions as unknown as Table<AnyRecord, string>,
  spec_section: () => db.spec_sections as unknown as Table<AnyRecord, string>,
  rfi: () => db.rfis as unknown as Table<AnyRecord, string>,
  site_visit: () => db.site_visits as unknown as Table<AnyRecord, string>,
  observation: () => db.observations as unknown as Table<AnyRecord, string>,
  valuation: () => db.valuations as unknown as Table<AnyRecord, string>,
  measurement: () => db.measurements as unknown as Table<AnyRecord, string>,
  sub_daily_report: () => db.sub_daily_reports as unknown as Table<AnyRecord, string>,
  warranty_claim: () => db.warranty_claims as unknown as Table<AnyRecord, string>,
  conversation: () => db.conversations as unknown as Table<AnyRecord, string>,
  message: () => db.messages as unknown as Table<AnyRecord, string>,
};

const schemas: Record<FieldEntityType, z.ZodType<{ id: string; project_id: string }>> = {
  stage: stageSchema,
  milestone: milestoneSchema,
  task: taskSchema,
  progress_update: progressUpdateSchema,
  issue: issueSchema,
  diary_entry: diaryEntrySchema,
  media_asset: mediaAssetSchema,
  project_worker: engagementSchema,
  attendance_event: attendanceEventSchema,
  worker_day: workerDaySchema,
  boq: boqSchema,
  boq_version: boqVersionSchema,
  boq_section: boqSectionSchema,
  boq_item: boqItemSchema,
  expense: expenseSchema,
  requisition: requisitionSchema,
  purchase_order: purchaseOrderSchema,
  delivery: deliverySchema,
  checklist_template: templateSchema,
  inspection_request: inspectionRequestSchema,
  inspection: inspectionSchema,
  inspection_result: inspectionResultSchema,
  ncr: ncrSchema,
  rectification: rectificationSchema,
  floor_plan: floorPlanSchema,
  snag: snagSchema,
  incident: incidentSchema,
  toolbox_talk: toolboxTalkSchema,
  toolbox_attendance: toolboxAttendanceSchema,
  zone: zoneSchema,
  crew: crewSchema,
  constraint: constraintSchema,
  daily_assignment: dailyAssignmentSchema,
  visit: visitSchema,
  action_item: actionItemSchema,
  stock_item: stockItemSchema,
  stock_movement: stockMovementSchema,
  material_request: materialRequestSchema,
  equipment: equipmentSchema,
  equipment_log: equipmentLogSchema,
  equipment_check: equipmentCheckSchema,
  drawing: drawingSchema,
  drawing_revision: drawingRevisionSchema,
  spec_section: specSectionSchema,
  rfi: rfiSchema,
  site_visit: siteVisitSchema,
  observation: observationSchema,
  valuation: valuationSchema,
  measurement: measurementSchema,
  sub_daily_report: subReportSchema,
  warranty_claim: warrantyClaimSchema,
  conversation: conversationSchema,
  message: messageSchema,
};

const config = (type: FieldEntityType): EntityConfig => ({
  table: tables[type](),
  schema: schemas[type],
  reduce: reducers[type],
});

export function isFieldEntity(type: string): type is FieldEntityType {
  return (FIELD_ENTITY_TYPES as readonly string[]).includes(type);
}

export function fieldTables(): Table[] {
  return FIELD_ENTITY_TYPES.map((type) => tables[type]());
}

/** Parse server data; `null` if it does not match the contract (logged in dev). */
export function parseEntity(type: FieldEntityType, data: unknown, fallbackProjectId?: string) {
  const input =
    data && typeof data === 'object' && fallbackProjectId && !('project_id' in data)
      ? { ...data, project_id: fallbackProjectId }
      : data;
  const parsed = schemas[type].safeParse(input);
  if (!parsed.success) {
    if (import.meta.env.DEV) console.debug('[sync] unparseable', type, parsed.error.issues);
    return null;
  }
  return parsed.data as Record<string, unknown> & { id: string; project_id: string };
}

/** Mutations for a record the server has not confirmed yet, oldest first. */
async function unconfirmed(type: FieldEntityType, id: string): Promise<OutboxRecord[]> {
  const rows = await listForEntity(type, id);
  return rows
    .filter((row) => row.state !== 'applied')
    .sort((a, b) => (a.mutation_id < b.mutation_id ? -1 : 1));
}

/**
 * Re-derive `data` from `server` + queued mutations. Call inside a
 * transaction that includes the entity table and the outbox.
 */
export async function recompute(type: FieldEntityType, id: string): Promise<void> {
  const { table, reduce, schema } = config(type);
  const record = await table.get(id);
  const pending = await unconfirmed(type, id);
  let data: Record<string, unknown> | null = (record?.server as Record<string, unknown>) ?? null;
  for (const mutation of pending) data = reduce(data, mutation);
  if (!data) {
    if (record) await table.delete(id);
    return;
  }
  const parsed = schema.safeParse(data);
  const shown = (parsed.success ? parsed.data : data) as AnyRecord['data'];
  await table.put({
    id,
    project_id: shown.project_id || record?.project_id || '',
    data: shown,
    server: record?.server ?? null,
    version: record?.version ?? null,
    local_state: pending.length ? 'pending' : 'synced',
    updated_at: now(),
  });
}

/** Record what the server says a record now is, then re-derive the shown copy. */
export async function setServerCopy(
  type: FieldEntityType,
  data: Record<string, unknown>,
  version: number | null,
  fallbackProjectId?: string,
): Promise<boolean> {
  const parsed = parseEntity(type, data, fallbackProjectId);
  if (!parsed) return false;
  const { table } = config(type);
  const existing = await table.get(parsed.id);
  await table.put({
    id: parsed.id,
    project_id: parsed.project_id,
    data: existing?.data ?? parsed,
    server: parsed,
    version: version ?? (typeof parsed['version'] === 'number' ? parsed['version'] : null),
    local_state: existing?.local_state ?? 'synced',
    updated_at: now(),
  });
  await recompute(type, parsed.id);
  return true;
}

/** Pull applier: upsert or tombstone. */
export async function applyFieldChange(change: PullChange, projectId: string): Promise<void> {
  if (!isFieldEntity(change.entity_type)) return;
  const type = change.entity_type;
  if (change.op === 'delete') {
    await tables[type]().delete(change.entity_id);
    return;
  }
  if (change.data) await setServerCopy(type, change.data, change.version, projectId);
}

/**
 * The server answered a create with a DIFFERENT record that already existed
 * (attendance: the same tap within two minutes, perhaps from another phone).
 * The local optimistic record is merged into it: later queued mutations and
 * unsent photos follow the server's id, and the optimistic copy disappears.
 * Call inside a transaction that includes the outbox, the media queue and the
 * entity table.
 */
async function mergeIntoExisting(
  type: FieldEntityType,
  localId: string,
  serverId: string,
): Promise<void> {
  await db.outbox
    .filter((row) => row.state !== 'applied')
    .modify((row) => {
      if (row.entity_type === type && row.entity_id === localId) row.entity_id = serverId;
      for (const [key, value] of Object.entries(row.payload)) {
        if (value === localId) row.payload[key] = serverId;
      }
    });
  await db.media_queue
    .where('[entity_type+entity_id]')
    .equals([type, localId])
    .modify((row) => {
      if (row.step === 'init') row.entity_id = serverId;
    });
  await recompute(type, localId);
  await recompute(type, serverId);
}

/** Push applier: the server accepted a mutation (and usually says what the record now is). */
export async function applyFieldPushResult(
  type: FieldEntityType,
  entityId: string,
  version: number | undefined,
  data: Record<string, unknown> | undefined,
  projectId: string | null,
): Promise<void> {
  if (data) {
    await setServerCopy(type, data, version ?? null, projectId ?? undefined);
    const serverId = typeof data['id'] === 'string' ? data['id'] : entityId;
    if (serverId !== entityId) await mergeIntoExisting(type, entityId, serverId);
    return;
  }
  const table = tables[type]();
  if (typeof version === 'number') await table.update(entityId, { version });
  await recompute(type, entityId);
}

/** Apply a response from an online-only endpoint (approve, verify, close…). */
export async function applyOnlineResult(
  type: FieldEntityType,
  data: { id: string; version: number | null },
): Promise<void> {
  await db.transaction('rw', [tables[type](), db.outbox], async () => {
    await setServerCopy(type, data as unknown as Record<string, unknown>, data.version);
  });
}

export type QueueInput = {
  entity_id: string;
  project_id: string;
  operation: MutationOperation;
  payload: Record<string, unknown>;
  /** Subject shown in the Sync Center (title, date…), not a sentence. */
  summary: string;
};

/**
 * Queue an offline-capable mutation and show its effect locally — one
 * transaction (see `recordChange`).
 */
export async function queueFieldMutation(
  type: FieldEntityType,
  input: QueueInput,
  options: { extraTables?: Table[]; also?: () => Promise<unknown> } = {},
): Promise<OutboxRecord> {
  const { table, reduce, schema } = config(type);
  return recordChange(
    [table, ...(options.extraTables ?? [])],
    async () => {
      const record = await table.get(input.entity_id);
      const next = reduce((record?.data as Record<string, unknown>) ?? null, input);
      if (!next) throw new Error(`Cannot ${input.operation} a ${type} that is not on this device.`);
      const parsed = schema.parse(next);
      await table.put({
        id: input.entity_id,
        project_id: input.project_id,
        data: parsed,
        server: record?.server ?? null,
        version: record?.version ?? null,
        local_state: 'pending',
        updated_at: now(),
      });
      await options.also?.();
    },
    {
      entity_type: type,
      entity_id: input.entity_id,
      operation: input.operation,
      base_version: (await table.get(input.entity_id))?.version ?? null,
      payload: input.payload,
      project_id: input.project_id,
      summary: input.summary,
    },
  );
}

// ---------------------------------------------------------------------------
// Three-way merge payloads
// ---------------------------------------------------------------------------

function same(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/**
 * `{changes, base}` for an update: only the fields the person actually
 * changed, each with the value they started editing from. The server merges
 * per field — untouched fields never clash.
 */
export function buildUpdate<T extends object>(
  original: T,
  edited: Partial<T>,
  fields: readonly (keyof T & string)[],
): { changes: Record<string, unknown>; base: Record<string, unknown> } | null {
  const changes: Record<string, unknown> = {};
  const base: Record<string, unknown> = {};
  for (const field of fields) {
    if (!(field in edited)) continue;
    const next = edited[field];
    if (same(next, original[field])) continue;
    changes[field] = next ?? null;
    base[field] = original[field] ?? null;
  }
  return Object.keys(changes).length ? { changes, base } : null;
}
